import { ISO_CODE_GERMANY, ISO_CODE_NETHERLANDS } from '@constants/countries';
import environment from '@utilities/environment';
import { getAssetsFolder } from '@utilities/get-assets-folder';
import { PickPartial } from '@utilities/types';

const TEST_GERMANY = false;
const LANGUAGE_ISO_CODE = TEST_GERMANY ? ISO_CODE_GERMANY : ISO_CODE_NETHERLANDS;
const LOCAL_DICTIONARY_PATH = TEST_GERMANY ? '/lang/de.json' : '/lang/nl.json';

type MockBaseProps = Pick<
    __ENVIRONMENT_DETAILS__,
    | 'environment'
    | 'languageIsoCode'
    | 'dictionaryUrl'
    | 'compareUrl'
    | 'areaHouseOverviewUrl'
    | 'mapbox'
>;

const defaultProps: MockBaseProps = {
    environment: 'local',
    dictionaryUrl: getAssetsFolder(LOCAL_DICTIONARY_PATH),
    languageIsoCode: LANGUAGE_ISO_CODE,
    compareUrl: '/compare-houses',
    areaHouseOverviewUrl: '/properties-overview',
    mapbox: {
        style: 'bpd-acc/ciowo3k3f000udum18y8x7qd0',
        accessToken:
            'pk.eyJ1IjoiYnBkLWFjYyIsImEiOiJjaWlzbzBreXcwMzk5dTZrbnNlMDY1ZTFhIn0.N4JONLqHs9Bc67kJTikkXw',
    },
};

type EnvironmentName = 'TEST' | 'ACC' | 'PROD';
type EnvironmentMockBaseProps = PickPartial<
    __ENVIRONMENT_DETAILS__,
    keyof MockBaseProps | 'apiBaseUrl' | 'logoutUrl'
>;

const defaultEnvProps: Record<EnvironmentName, EnvironmentMockBaseProps> = {
    TEST: {
        ...defaultProps,
        apiBaseUrl: 'https://hoefenhaag.test.bpd.nl/api/middleware',
        logoutUrl: 'https://acceptance-bpd.cs189.force.com/MijnEigenHuis/secur/logout.jsp',
    },
    ACC: {
        ...defaultProps,
        apiBaseUrl: 'https://hoefenhaag.acc.bpd.nl/api/middleware',
        logoutUrl: 'https://acceptance-bpd.cs189.force.com/MijnEigenHuis/secur/logout.jsp',
    },
    PROD: {
        ...defaultProps,
        apiBaseUrl: 'https://hoefenhaag.nl/api/middleware',
        logoutUrl: 'https://www.mijneigenhuis.nl/secur/logout.jsp',
        mapbox: {
            style: 'bpd/cj47zzkvg1jo62roansv1k0mo',
            accessToken:
                'pk.eyJ1IjoiYnBkIiwiYSI6IjBiNTQyNmIyNTFmNTk4NDdkOTE1MDMyNDViYWRmMDI1In0.tfd0_36gpomlERjFuqPIsQ',
        },
    },
};

type EnvironmentDetailsProps = Partial<__ENVIRONMENT_DETAILS__>;
type EnvironmentMockAreaProps = PickPartial<
    __ENVIRONMENT_DETAILS__,
    'areaProjectName' | 'areaId' | 'districtHouseOverviewUrl'
>;
type EnvironmentMocker = (props?: EnvironmentDetailsProps) => void;
type EnvironmentMockerGenerator = (
    areaProps: EnvironmentMockAreaProps,
    envProps?: Partial<Record<EnvironmentName, EnvironmentDetailsProps>>,
) => Record<EnvironmentName, EnvironmentMocker>;

const MOCK_NODE_ID = 'mockNodeId';
const baseApiMock: Record<EnvironmentName, string> = {
    TEST: 'https://hoefenhaag.test.bpd.nl',
    ACC: 'https://hoefenhaag.acc.bpd.nl',
    PROD: 'https://hoefenhaag.nl',
};

const createMocks: EnvironmentMockerGenerator = (areaProps, envProps) => {
    return {
        TEST: (extraProps) => {
            environment.baseApiUrl = baseApiMock.TEST;
            window.__ENVIRONMENT_DETAILS__ = {
                nodeId: MOCK_NODE_ID,
                ...defaultEnvProps.TEST,
                ...areaProps,
                ...envProps?.TEST,
                ...extraProps,
            };
        },
        ACC: (extraProps) => {
            environment.baseApiUrl = baseApiMock.ACC;
            window.__ENVIRONMENT_DETAILS__ = {
                nodeId: MOCK_NODE_ID,
                ...defaultEnvProps.ACC,
                ...areaProps,
                ...envProps?.ACC,
                ...extraProps,
            };
        },
        PROD: (extraProps) => {
            environment.baseApiUrl = baseApiMock.PROD;
            window.__ENVIRONMENT_DETAILS__ = {
                nodeId: MOCK_NODE_ID,
                ...defaultEnvProps.PROD,
                ...areaProps,
                ...envProps?.PROD,
                ...extraProps,
            };
        },
    };
};

const hoefenHaag: EnvironmentMockAreaProps = {
    areaId: 'a0iw0000008QvIVAA0',
    areaProjectName: 'Hoef en Haag',
    districtHouseOverviewUrl: {
        a0lw000000EBLa4AAH: '/het-dorpshart/woningen',
        a0lw000000EBvWTAA1: '/het-lint/woningen',
        a0lw000000EBvWYAA1: '/de-erven/woningen',
    },
};

const digitalPark: EnvironmentMockAreaProps = {
    areaId: 'a0i7R00001qWMD9QAO',
    areaProjectName: 'Digital Park',
    districtHouseOverviewUrl: {
        a0l7R00000dUs1zQAC: '/department-digitals/woningen',
        a0l7R00000gK6fuQAC: '/department-digital-rental/woningen',
    },
};

const mocks = {
    HoefenHaag: createMocks(hoefenHaag),
    DeErven: createMocks({
        ...hoefenHaag,
        districtId: 'a0lw000000EBvWYAA1',
    }),
    DigitalPark: createMocks(digitalPark),
    DepartmentDigitalProjectPage: createMocks(digitalPark, {
        TEST: {
            districtId: 'a0l7R00000dUs1zQAC',
            projectId: 'a067R00002zmK6YQAU',
        },
    }),
    DeNieuweDefensie: createMocks({
        areaId: 'a0i2X00000Cf6aQQAR',
        districtId: 'a0l2X00000YRioKQAT',
        areaProjectName: 'De Nieuwe Defensie',
        districtHouseOverviewUrl: {
            a0l2X00000YRioKQAT: '/woningen',
        },
    }),
};

export default mocks;
